import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AlertCircle, Loader2 } from "lucide-react";

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleCallback, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous requests
    if (processedRef.current) return;
    
    const guid = searchParams.get("guid");
    const returnUrl = searchParams.get("returnUrl") || "/";

    if (!guid) {
      setError("Missing authentication token");
      return;
    }

    processedRef.current = true;
    
    const processCallback = async () => {
      try {
        const redirectTo = await handleCallback(guid, returnUrl);
        navigate(redirectTo, { replace: true });
      } catch (err: any) {
        setError(err.message || "Authentication failed");
        processedRef.current = false; // Allow retry on error
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleRetry = async () => {
    const returnUrl = searchParams.get("returnUrl") || "/";
    await login(returnUrl);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900">
        <div className="max-w-md w-full bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Error
            </h1>
            <p className="text-gray-600 dark:text-neutral-400 mb-6">{error}</p>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-600 dark:text-neutral-400">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
